import { z } from 'zod';

/**
 * The Teams app (AI3, APP-089). The assistant answers in Teams with Adaptive Cards; the server
 * builds them from the same content as its emails, and the local test chat (Admin › Teams test
 * chat) renders exactly what Teams would get. Only the parts MyRewards writes are described here:
 * a card from anywhere else is never rendered.
 */

/**
 * What a card button sends back when pressed (`Action.Submit` data, which Teams returns as the
 * activity's `value`). It names one of MyRewards' own records, never an award or an amount: the
 * server looks up what the button was for, and checks it belongs to the person pressing it.
 * - `confirm`, `cancel`, `send`, `add_options`, `edit`: the "Here's what I heard" proposal `ref`,
 *   with the first 16 characters of its hash (`check`), so a stale card can't confirm a newer one.
 * - `approve`, `snooze`: the reminder message `ref` the button was on.
 * - `feedback_reply` (APP-138 §D5): Send reply on a feedback card, whose `ref` is the card itself;
 *   Teams sends the words in its box back beside the button's own data, as `reply`.
 * - `feedback_pick`, `feedback_other` (APP-138 §D17): on the read-back of words for the MyRewards
 *   team, the pending reply `ref` with its `check`, like `send`; a pick also names one of the
 *   person's own open reports (`report`), checked again to be theirs and open.
 */
export const TeamsButton = z.looseObject({
  mr: z.literal(1),
  act: z.enum([
    'confirm',
    'cancel',
    'send',
    'add_options',
    'edit',
    'approve',
    'snooze',
    'feedback_reply',
    'feedback_pick',
    'feedback_other',
  ]),
  ref: z.uuid(),
  check: z
    .string()
    .regex(/^[0-9a-f]{16}$/)
    .optional(),
  report: z
    .string()
    .regex(/^FB-\d{4}-\d{4,}$/)
    .optional(),
});
export type TeamsButton = z.infer<typeof TeamsButton>;

const Spacing = z.enum(['None', 'Small', 'Medium', 'Large']);

export const TeamsTextBlock = z.object({
  type: z.literal('TextBlock'),
  text: z.string().max(4000),
  wrap: z.literal(true),
  size: z.enum(['Small', 'Default', 'Medium', 'Large']).optional(),
  weight: z.enum(['Default', 'Bolder']).optional(),
  isSubtle: z.boolean().optional(),
  spacing: Spacing.optional(),
});
export type TeamsTextBlock = z.infer<typeof TeamsTextBlock>;

/**
 * One line of edited wording with bold words in it (APP-137 D8). A `TextRun` reads no markdown,
 * so a name or a note can't turn into a link; bold is the run's weight.
 */
export const TeamsRichTextBlock = z.object({
  type: z.literal('RichTextBlock'),
  inlines: z
    .array(
      z.object({
        type: z.literal('TextRun'),
        text: z.string().max(4000),
        weight: z.enum(['Default', 'Bolder']).optional(),
      }),
    )
    .min(1)
    .max(100),
  spacing: Spacing.optional(),
});
export type TeamsRichTextBlock = z.infer<typeof TeamsRichTextBlock>;

export const TeamsFactSet = z.object({
  type: z.literal('FactSet'),
  facts: z.array(z.object({ title: z.string().max(200), value: z.string().max(2000) })).max(40),
  spacing: Spacing.optional(),
});

export const TeamsColumnSet = z.object({
  type: z.literal('ColumnSet'),
  columns: z
    .array(
      z.object({
        type: z.literal('Column'),
        width: z.enum(['auto', 'stretch']),
        items: z.array(TeamsTextBlock).max(4),
      }),
    )
    .min(1)
    .max(4),
  spacing: Spacing.optional(),
  separator: z.boolean().optional(),
});

export const TeamsContainer = z.object({
  type: z.literal('Container'),
  style: z.enum(['default', 'emphasis']).optional(),
  items: z
    .array(z.union([TeamsTextBlock, TeamsRichTextBlock, TeamsFactSet, TeamsColumnSet]))
    .max(60),
  spacing: Spacing.optional(),
});

/**
 * The one box a card has (APP-138 §D5): the reply on a feedback card, which Teams returns with
 * Send reply as `reply`. Never more than a reply holds.
 */
export const TeamsInputText = z.object({
  type: z.literal('Input.Text'),
  id: z.literal('reply'),
  label: z.string().min(1).max(200),
  placeholder: z.string().max(200).optional(),
  isMultiline: z.literal(true),
  maxLength: z.literal(4000),
  spacing: Spacing.optional(),
});
export type TeamsInputText = z.infer<typeof TeamsInputText>;

export const TeamsCardAction = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Action.Submit'),
    title: z.string().min(1).max(80),
    data: TeamsButton,
    style: z.enum(['positive', 'destructive']).optional(),
  }),
  z.object({
    type: z.literal('Action.OpenUrl'),
    title: z.string().min(1).max(80),
    url: z.url(),
  }),
]);
export type TeamsCardAction = z.infer<typeof TeamsCardAction>;

/**
 * A row of buttons inside the card (APP-138 §D17): the assistant's chooser of the person's own
 * open feedback, one button each, more than the card's own six. Top level only.
 */
export const TeamsActionSet = z.object({
  type: z.literal('ActionSet'),
  actions: z.array(TeamsCardAction).min(1).max(10),
  spacing: Spacing.optional(),
});
export type TeamsActionSet = z.infer<typeof TeamsActionSet>;

export const TeamsCardElement = z.union([
  TeamsTextBlock,
  TeamsRichTextBlock,
  TeamsFactSet,
  TeamsColumnSet,
  TeamsContainer,
  TeamsInputText,
  TeamsActionSet,
]);
export type TeamsCardElement = z.infer<typeof TeamsCardElement>;

export const TeamsCard = z.object({
  type: z.literal('AdaptiveCard'),
  $schema: z.literal('http://adaptivecards.io/schemas/adaptive-card.json'),
  version: z.literal('1.5'),
  body: z.array(TeamsCardElement).max(80),
  actions: z.array(TeamsCardAction).max(6).optional(),
  msteams: z.object({ width: z.literal('Full') }).optional(),
});
export type TeamsCard = z.infer<typeof TeamsCard>;

// ---- Admin › Teams test chat (local only) ---------------------------------------------------

/** One message in the test chat: what the person wrote (or pressed), or the assistant's card. */
export const TeamsChatMessage = z.object({
  id: z.uuid(),
  from: z.enum(['person', 'assistant']),
  at: z.string(),
  /** What the person typed, or the assistant's plain text when it sent no card. */
  text: z.string().nullable(),
  card: TeamsCard.nullable(),
  /** The button the person pressed, by its title, when this message was a press. */
  pressed: z.string().nullable(),
});
export type TeamsChatMessage = z.infer<typeof TeamsChatMessage>;

/** Whether this System Admin can use the test chat here, and why not. */
export const TeamsTestChatStatus = z.object({
  available: z.boolean(),
  reason: z.enum(['environment', 'testing_off', 'not_tester']).nullable(),
});
export type TeamsTestChatStatus = z.infer<typeof TeamsTestChatStatus>;

export const TeamsTestChatQuery = z.object({ personId: z.uuid() });
export type TeamsTestChatQuery = z.infer<typeof TeamsTestChatQuery>;

/** The test chat with one person: their messages and the assistant's cards, oldest first. */
export const TeamsTestChat = z.object({
  person: z.object({ id: z.uuid(), name: z.string() }),
  messages: z.array(TeamsChatMessage),
});
export type TeamsTestChat = z.infer<typeof TeamsTestChat>;

/** Write as the person, or press a button on one of the assistant's cards. */
export const TeamsTestChatSend = z
  .object({
    personId: z.uuid(),
    text: z.string().trim().min(1).max(2000).optional(),
    /** Exactly what Teams sends back for a press; the chat shows the title from the card. */
    button: TeamsButton.optional(),
  })
  .strict()
  .refine((v) => (v.text === undefined) !== (v.button === undefined), {
    message: 'Write a message or press a button, one at a time.',
  });
export type TeamsTestChatSend = z.infer<typeof TeamsTestChatSend>;

export const TeamsTestChatRemind = z.object({ personId: z.uuid() }).strict();
export type TeamsTestChatRemind = z.infer<typeof TeamsTestChatRemind>;

/** What the test chat's "Send the reminders" did: how many reminder cards it posted. */
export const TeamsTestChatReminders = z.object({ posted: z.int().nonnegative() });
export type TeamsTestChatReminders = z.infer<typeof TeamsTestChatReminders>;
